import { create } from "zustand";
import { axiosHelper, tokenHelper } from "../helpers";

export const useSplashStore = create((set, get) => ({
  splashes: [],
  splash: null,
  pagination: null,
  isLoading: false,
  isError: false,
  error: undefined,
  lastQuery: {
    page: 1,
    perPage: 10,
    search: "",
  },
  // Get all splashes
  getSplashes: async ({ page = 1, perPage = 10, search = "" } = {}) => {
    try {
      set({ isLoading: true, isError: false });
      
      // Save query params for later use
      set({ lastQuery: { page, perPage, search } });

      const accessToken = await tokenHelper.getToken();
      const params = new URLSearchParams({ page, perPage });
      if (search && search.trim() !== "") {
        params.append("search", search.trim());
      }

      const response = await axiosHelper.get(`/splash?${params.toString()}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      set({
        splashes: response.splashes || response.data || response || [],
        pagination: response.pagination,
        isLoading: false,
        isError: false,
      });

      return response.splashes || response.data || response || [];
    } catch (error) {
      console.error("Error fetching splashes:", error);
      set({ 
        splashes: [], 
        isLoading: false, 
        isError: true, 
        error: error.message 
      });
      return [];
    }
  },
  // Get single splash by ID
  getSplash: async (splashId) => {
    try {
      set({ isLoading: true, isError: false });
      const accessToken = await tokenHelper.getToken();
      
      const response = await axiosHelper.get(`/splash/${splashId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      set({ 
        splash: response, 
        isLoading: false, 
        isError: false 
      });
      return response;
    } catch (error) {
      console.error("Error fetching splash:", error);
      set({ 
        splash: null, 
        isLoading: false, 
        isError: true, 
        error: error.message 
      });
      throw error;
    }
  },
  // Create splash
  createSplash: async (body) => {
    try {
      set({ isLoading: true });
      const accessToken = await tokenHelper.getToken();

      const response = await axiosHelper.post(
        `/splash`,
        body,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      // Refresh the list with last query params
      const { page, perPage, search } = get().lastQuery;
      await get().getSplashes({ page, perPage, search });

      return response;
    } catch (error) {
      console.error("Error creating splash:", error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  // Update splash
  updateSplash: async (splashId, body) => {
    try {
      set({ isLoading: true });
      const accessToken = await tokenHelper.getToken();

      const response = await axiosHelper.put(
        `/splash/${splashId}`,
        body,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      // Refresh the list with last query params
      const { page, perPage, search } = get().lastQuery;
      await get().getSplashes({ page, perPage, search });

      return response;
    } catch (error) {
      console.error("Error updating splash:", error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  // Delete splash
  deleteSplash: async (splashId) => {
    try {
      set({ isLoading: true });
      const accessToken = await tokenHelper.getToken();

      await axiosHelper.delete(`/splash/${splashId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      // Refresh the list with last query params
      const { page, perPage, search } = get().lastQuery;
      await get().getSplashes({ page, perPage, search });

      return true;
    } catch (error) {
      console.error("Error deleting splash:", error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
}));

